import { CircularProgress } from "@mui/material";
import * as Sentry from "@sentry/react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslations } from "use-intl";

import { PurchaseStatus } from "@/app/payments/types";
import { BookingError } from "@/components/Errors/BookingError";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { OTPPopup } from "@/components/OTPPopup/OTPPopup";
import { PriceChangedModal } from "@/components/PriceChangedModal/PriceChangedModal";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { usePurchaseStateContext } from "@/contexts/PurchaseStateContext";
import {
  HotelBookingSummaryForm,
  type SummaryFormValues,
} from "@/features/hotels/components/hotel/HotelBookingSummaryForm";
import { HotelOfferError } from "@/features/hotels/components/hotel/HotelOfferError";
import { useHotelBookingContext } from "@/features/hotels/contexts/HotelBookingContext";
import { useHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { useHotelBooking } from "@/features/hotels/hooks/useHotelBooking";
import {
  useHotelRoomAvailabilityMutation,
  type UseHotelRoomAvailabilityMutationParams,
} from "@/features/hotels/hooks/useHotelRoomAvailability";
import { useHotelSearch } from "@/features/hotels/hooks/useHotelSearch";
import { calculateBookingPrice } from "@/features/hotels/utils/calculateBookingPrice";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { transformHotelTraveler } from "@/features/travellers/utils/transformTraveler";
import { usePromoCodeValidation } from "@/hooks/usePromoCodeValidation";
import { HotelTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import { formatCurrency } from "@/utils/currency";

export const Route = createFileRoute(
  "/_authenticated/_hotels/_hotel-offer/hotels/$id/summary",
)({
  component: RouteComponent,
  staticData: {
    hotelStep: { stepNumber: 3 },
    drawer: true,
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const params = Route.useParams();

  const t = useTranslations();

  const { searchResultKey, searchResultToken } = useHotelSearch();
  const {
    data: booking,
    availabilityToken,
    promoCode,
    setPromoCode,
  } = useHotelBookingContext();
  const { selectedTravellers } = useTravellersSelectionContext();
  const { data: hotel } = useHotelDataContext();
  const { updatePurchaseState } = usePurchaseStateContext();
  const { searchData } = useHotelSearchDataContext();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formValues, setFormValues] = useState<SummaryFormValues | null>(null);
  const [showOTP, setShowOTP] = useState(false);
  const { paymentType, sessionId, refreshSession } = usePaymentContext();

  const {
    data: promoCodeData,
    validationStatus: promoCodeValidationStatus,
    revalidate,
  } = usePromoCodeValidation({
    service: "hotels",
    validationRequestData: {
      code: promoCode || "",
      availabilityToken: availabilityToken || "",
    },
  });

  const travelDate = new Date(searchData!.dates.checkIn);

  const paymentMethod = paymentType === "bnpl" ? "bnpl" : "qiAccount";

  const {
    qiPayment: { isPending: isQiPaymentPending, isError, error, mutateAsync },
    checkoutPayment: {
      submit,
      isPending: isCheckoutPaymentPending,
      isLoadingCheckoutForm,
    },
    bnplPayment: {
      isPending: isBnplPaymentPending,
      mutateAsync: bnplMutateAsync,
      isError: isBnplPaymentError,
    },
  } = useHotelBooking({
    method: paymentMethod,
    initialAvailabilityToken: availabilityToken,
    onSuccess: ({ orderId, status }) => {
      if (status === PurchaseStatus.SUCCESSFUL) {
        if (paymentType === "bnpl") {
          // to update eligibility info
          refreshSession();
        }
        navigate({
          to: "/hotels/$id/confirmation",
          search: { orderId },
        }).then(() => {
          updatePurchaseState("completed");
        });
      }
    },
  });

  const handleRefetchAvailabilitySuccess: UseHotelRoomAvailabilityMutationParams["onSuccess"] =
    (data) => {
      if (data && booking) {
        const latestTotalPrice = data.totalPrice;
        if (latestTotalPrice !== calculateBookingPrice(booking).value) {
          revalidate();
          setShowConfirmation(true);
        } else {
          handlePurchase(availabilityToken);
        }
      }
    };

  const {
    data: availability,
    isError: isAvailabilityError,
    error: availabilityError,
    isPending: isAvailabilityRefetching,
    mutate: refetchAvailability,
  } = useHotelRoomAvailabilityMutation({
    onSuccess: handleRefetchAvailabilitySuccess,
  });

  const handleSubmit = (values: SummaryFormValues) => {
    setFormValues(values);
    if (!searchResultKey || !searchResultToken || !booking) return;
    refetchAvailability({
      hotelId: `${hotel?.id}`,
      searchResultKey,
      searchResultToken,
      packageToken: booking.packageToken,
      offerId: booking.offerId,
      roomTokens: booking.rooms.map((room) => room.roomToken),
    });
  };

  const createRoomAssignments = (formValues: SummaryFormValues) => {
    return Array.from(Object.entries(formValues.roomAssignments)).map(
      ([packageRoomToken, assignment]) => ({
        packageRoomToken,
        travelers: assignment.travelers.map((t) => {
          const traveler = selectedTravellers.find((trav) => trav.id === t);

          return transformHotelTraveler(
            traveler!,
            {
              isLead: formValues.leadTraveler === t,
            },
            travelDate,
          );
        }),
      }),
    );
  };

  const promoCodeToApply = promoCodeData?.isValid
    ? promoCode || undefined
    : undefined;

  const handlePurchase = (availabilityToken?: string | null) => {
    if (
      !formValues ||
      !searchResultKey ||
      !searchResultToken ||
      !hotel ||
      !booking
    )
      return;

    const totalPrice = calculateBookingPrice(booking);

    rudderAnalytics.track(HotelTrackingEvents.USER_PROCEED_TO_PAYMENT_SCREEN, {
      amount: totalPrice.value,
      payment_method: paymentMethod,
      currency: totalPrice.currency,
    });
    if (paymentMethod === "bnpl") {
      setShowOTP(true);
    } else if (paymentMethod === "qiAccount") {
      mutateAsync({
        promoCode: promoCodeToApply,
        availabilityToken: availabilityToken || "",
        searchResultKey,
        searchResultToken,
        hotelId: hotel.id,
        offerId: booking.offerId,
        checkInDate: travelDate,
        rooms: createRoomAssignments(formValues),
      });
    } else {
      submit({
        promoCode: promoCodeToApply,
        availabilityToken: availabilityToken || "",
        searchResultKey,
        searchResultToken,
        hotelId: hotel.id,
        offerId: booking.offerId,
        checkInDate: travelDate,
        rooms: createRoomAssignments(formValues),
      });
    }
  };

  const handleOTPSubmit = async (otp: string) => {
    if (
      !formValues ||
      !searchResultKey ||
      !searchResultToken ||
      !hotel ||
      !booking ||
      !availabilityToken ||
      !sessionId
    ) {
      return;
    }

    await bnplMutateAsync({
      promoCode: promoCodeToApply,
      availabilityToken,
      searchResultKey,
      searchResultToken,
      sessionId,
      otp,
      hotelId: hotel.id,
      offerId: booking.offerId,
      checkInDate: travelDate,
      rooms: createRoomAssignments(formValues),
    });
  };

  const handleCancelPriceChange = () => {
    setShowConfirmation(false);
    navigate({
      to: "/",
      search: { category: "hotels" },
    });
  };

  if (
    !availabilityToken ||
    !booking ||
    !hotel ||
    !searchResultKey ||
    !searchResultToken
  ) {
    // If token does not exist at this point, or other required params, redirect to the hotel offer page
    // This is a fallback in case the user tries to access this page directly
    // without going through the hotel offer page
    // It's unlikely that this will happen, but it's a good fallback to have
    // Avoids the need to handle these cases in the HotelBookingSummaryForm component, and other components
    Sentry.captureMessage(
      "User tried to access hotel summary page directly without going through the hotel offer page",
      "fatal",
    );
    return <Navigate to="/hotels/$id/travelers" params={params} />;
  }

  if (isAvailabilityError) {
    return <HotelOfferError error={availabilityError} />;
  }

  if (isError) {
    return <BookingError error={error} type="hotels" />;
  }

  if (isQiPaymentPending || isCheckoutPaymentPending || isBnplPaymentPending) {
    return (
      <PageLoader>
        <AnimatedLoader icon="buildings" />
      </PageLoader>
    );
  }

  return (
    <div className="px-4">
      <div className="mb-4 text-base font-medium">
        {t("hotels.bookingSummary")}
      </div>
      <div className="section-title mb-2">
        {t("hotels.overview.hotelDetails")}
      </div>
      <HotelBookingSummaryForm
        hotel={hotel}
        booking={booking}
        handleSubmit={handleSubmit}
        promoCode={promoCode}
        promoCodeData={promoCodeData}
        promoCodeValidationStatus={promoCodeValidationStatus}
        setPromoCode={setPromoCode}
        travelDate={travelDate}
        rooms={searchData!.rooms}
        loading={isAvailabilityRefetching}
        selectedTravellers={selectedTravellers}
      />
      {isLoadingCheckoutForm ? <CircularProgress size={20} /> : null}
      <div id="flow-container" className="section-card p-0 not-empty:mt-2" />
      <PriceChangedModal
        open={showConfirmation}
        price={formatCurrency(availability?.data.data.totalPrice)}
        onCancel={handleCancelPriceChange}
        onConfirm={() => {
          handlePurchase(availability?.data.data.availabilityToken);
        }}
      />
      <OTPPopup
        isOpen={showOTP}
        onClose={() => {
          setShowOTP(false);
        }}
        onSubmit={handleOTPSubmit}
        error={isBnplPaymentError ? t("payment.otpError") : undefined}
      />
    </div>
  );
}
